'use client'
import React, { useEffect, useMemo, useState } from 'react'
import Cropper, { type Area, type Point } from 'react-easy-crop'
import { ModalGeneric } from '../modal-generic'
import { cropImageToFile, type ImageCropOptions } from './crop-image'

const MIN_ZOOM = 1
const MAX_ZOOM = 4
const ZOOM_STEP = 0.05
const INITIAL_CROP: Point = { x: 0, y: 0 }

export interface ImageCropperModalProps {
  /** Fichero a editar. El modal se muestra mientras sea distinto de null. */
  file: File | null
  onConfirm: (croppedFile: File) => void | Promise<void>
  onCancel: () => void
  options?: ImageCropOptions
  title?: string
}

/**
 * Editor mínimo de imagen: recortar (marco fijo + zoom) y reposicionar (arrastrar).
 * Devuelve el resultado como File PNG a través de `onConfirm`.
 */
export function ImageCropperModal({
  file,
  onConfirm,
  onCancel,
  options,
  title = 'Ajustar imagen',
}: ImageCropperModalProps) {
  const { aspect = 1, shape = 'rect', outputMaxSize } = options ?? {}

  const imageSrc = useMemo(
    () => (file ? URL.createObjectURL(file) : null),
    [file]
  )
  useEffect(() => {
    if (!imageSrc) return
    return () => URL.revokeObjectURL(imageSrc)
  }, [imageSrc])

  const [crop, setCrop] = useState<Point>(INITIAL_CROP)
  const [zoom, setZoom] = useState(MIN_ZOOM)
  const [croppedArea, setCroppedArea] = useState<Area | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Cada fichero nuevo empieza centrado y sin zoom
  useEffect(() => {
    setCrop(INITIAL_CROP)
    setZoom(MIN_ZOOM)
    setCroppedArea(null)
    setError(null)
  }, [file])

  const handleConfirm = async () => {
    if (!file || !imageSrc || !croppedArea) return
    setIsProcessing(true)
    setError(null)
    try {
      const cropped = await cropImageToFile(
        imageSrc,
        croppedArea,
        file.name,
        outputMaxSize
      )
      await onConfirm(cropped)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo procesar la imagen')
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <ModalGeneric
      isOpen={file !== null}
      handleOpenChange={(open) => !open && !isProcessing && onCancel()}
      onCloseModal={onCancel}
      onSubmit={handleConfirm}
      onSubmitSecondary={onCancel}
      isLoading={isProcessing}
      isButtonDisabled={!croppedArea}
      title={title}
      buttonContent="Guardar"
      secondaryButtonContent="Cancelar">
      {imageSrc && (
        <>
          <div className="relative h-[360px] w-full overflow-hidden rounded-lg bg-gray-900">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              cropShape={shape}
              minZoom={MIN_ZOOM}
              maxZoom={MAX_ZOOM}
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, areaPixels) => setCroppedArea(areaPixels)}
            />
          </div>

          <label className="flex items-center gap-3 text-sm text-gray-700">
            <span className="w-16 shrink-0">Recorte</span>
            <input
              type="range"
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={ZOOM_STEP}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              aria-label="Nivel de recorte"
              className="w-full accent-admin-blue"
            />
          </label>

          <p className="text-xs text-gray-500">
            Arrastra la imagen para colocarla y usa el control para ajustar el
            recorte.
          </p>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </>
      )}
    </ModalGeneric>
  )
}
